import { useEffect, useState } from 'react';
import defaults from './defaults.json';

const basePath = new URL(process.env.PUBLIC_URL || '/', window.location.origin).pathname.replace(/\/$/, '');
export const sitePath = path => basePath + '/' + path.replace(/^\.?\//, '');

export function safeLink(value) {
  if (typeof value !== 'string') return undefined;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : undefined;
  } catch { return undefined; }
}

export function imageUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  if (/^https?:\/\//i.test(value)) return safeLink(value);
  if (/[:\\]/.test(value) || [...value].some(char => char.charCodeAt(0) < 32) || value.startsWith('//')) return undefined;
  return sitePath(value);
}

// Preserve intentionally empty lists and false toggles. Reject malformed documents.
export function normalizeContent(template, value, inList = false) {
  if (value === null && typeof template === 'string') return '';
  if (Array.isArray(template)) {
    if (value === null) return [];
    if (!Array.isArray(value)) throw new Error('Expected a content list');
    return value.map(item => normalizeContent(template[0], item, true));
  }
  if (template && typeof template === 'object') {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Expected content fields');
    return Object.fromEntries(Object.entries(template).map(([key, sample]) => {
      const fallback = inList && typeof sample === 'string' ? '' : sample;
      return [key, normalizeContent(sample, value[key] === undefined ? fallback : value[key], inList)];
    }));
  }
  if (typeof template === 'string' && typeof value === 'number') return String(value);
  if (typeof value !== typeof template) throw new Error('Invalid content field');
  return value;
}

export default function useSiteContent(name) {
  const [state, setState] = useState({ data: defaults[name], unavailable: false });
  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    let active = true;
    setState({ data: defaults[name], unavailable: false });
    fetch(sitePath('content/' + name + '.json') + '?v=' + Date.now(), {
      cache: 'no-store', signal: controller.signal,
    })
      .then(response => {
        if (!response.ok) throw new Error('Content unavailable');
        return response.json();
      })
      .then(value => {
        const data = normalizeContent(defaults[name], value);
        if (active) setState({ data, unavailable: false });
      })
      .catch(() => {
        if (active) setState({ data: defaults[name], unavailable: true });
      })
      .finally(() => clearTimeout(timeout));
    return () => { active = false; clearTimeout(timeout); controller.abort(); };
  }, [name]);
  return state;
}
